import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Stack, Container } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { authService } from '../services/api';
import LeaveBalanceWidget from './Leave/LeaveBalanceWidget';
import HelpCard from './Dashboard/HelpCard';

export default function EmployeeDashboard() {
  const { accounts } = useMsal();
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authService.getProfile();
        setUserProfile(response.data);
      } catch (error) {
        console.warn('Could not fetch user profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Get display name from MSAL accounts or profile
  const getDisplayName = () => {
    if (userProfile?.displayName) return userProfile.displayName;
    if (userProfile?.employee?.firstName && userProfile?.employee?.lastName) {
      return `${userProfile.employee.firstName} ${userProfile.employee.lastName}`;
    }
    return accounts[0]?.name || 'there';
  };
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
          Welcome {getDisplayName()}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
          Your personal workspace for managing leave requests and tracking your time off balance.
        </Typography>
      </Box>

      {/* Main Content Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
        gap: 3,
        alignItems: 'start'
      }}>
        {/* Primary Content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <LeaveBalanceWidget />

          {/* Quick Actions */}
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            '& .MuiCardContent-root': { pb: '16px !important' }
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Quick Actions
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                Get things done faster with these shortcuts
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to="/leave"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  Request Leave
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Help Card */}
          <HelpCard />

          {/* Tips Card */}
          <Card sx={{
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                💡 Tips
              </Typography>
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  • Submit leave requests at least 2 weeks in advance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Check your balance before planning extended time off
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Contact HR for questions about leave policies
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
}

